import { Layer, PatternElement } from '../types';
import { generatePatternElements } from './patternGenerator';

// パラメーター文字列の生成
function generateParameterString(layer: Layer): string {
  const params = layer.parameters;
  return `_size${params.size}_gap${params.gapX}x${params.gapY}_angle${
    params.angle
  }_view${params.angleView}_color${params.color.replace('#', '')}`;
}

// 日付をJSTで整形する新しい関数
function formatJSTDateTime(date: Date): string {
  // UTCからJSTへの変換（+9時間）
  const jstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000);

  // 各部分を2桁の文字列に整形する補助関数
  const pad = (num: number): string => num.toString().padStart(2, '0');

  const year = jstDate.getUTCFullYear();
  const month = pad(jstDate.getUTCMonth() + 1); // 月は0から始まるため+1
  const day = pad(jstDate.getUTCDate());
  const hours = pad(jstDate.getUTCHours());
  const minutes = pad(jstDate.getUTCMinutes());
  const seconds = pad(jstDate.getUTCSeconds());

  // YYYYMMDD_HHMMSSの形式で返す
  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
}

// SVGファイル名の生成
function generateFileName(layers: Layer[]): string {
  // 現在の日時をJSTで取得
  const jstDateStr = formatJSTDateTime(new Date());

  // レイヤーごとのパラメーター文字列を生成
  const layerParams = layers
    .map(
      (layer, index) => `_layer${index + 1}${generateParameterString(layer)}`
    )
    .join('');

  return `pattern_${jstDateStr}${layerParams}.svg`;
}

// 画像をBase64形式に変換する関数
function getBase64Image(imageUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
}

function createPatternDef(
  svgString: string,
  color: string,
  id: string
): string {
  try {
    // SVGドキュメントのパース
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');

    // エラーチェック
    const parserError = svgDoc.getElementsByTagName('parsererror');
    if (parserError.length > 0) {
      console.error('SVG parsing error:', parserError[0].textContent);
      return '';
    }

    const svgElement = svgDoc.documentElement;
    const contentClone = svgElement.cloneNode(true) as SVGElement;

    // viewBox属性の処理
    let viewBox = svgElement.getAttribute('viewBox');
    if (!viewBox) {
      const width = svgElement.getAttribute('width') || '100';
      const height = svgElement.getAttribute('height') || '100';
      viewBox = `0 0 ${width} ${height}`;
    }

    // 16進数カラーコードをRGBA形式に変換
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const rgbaColor = `rgb(${r}, ${g}, ${b})`;

    // 色を適用する関数を関数式として定義
    const processElement = (element: Element): void => {
      // svg要素自体はスキップ
      if (element.tagName.toLowerCase() === 'svg') {
        Array.from(element.children).forEach(processElement);
        return;
      }

      // fill属性の処理
      const currentFill = element.getAttribute('fill');
      // fillが設定されていないか、black/#000000の場合のみ新しい色を適用
      if (
        !currentFill ||
        currentFill === '' ||
        currentFill === '#000000' ||
        currentFill === '#000' ||
        currentFill === 'black' ||
        currentFill === 'rgb(0, 0, 0)'
      ) {
        element.setAttribute('fill', rgbaColor);
      }

      // stroke属性の処理
      const currentStroke = element.getAttribute('stroke');
      // strokeが設定されていないか、black/#000000の場合のみ新しい色を適用
      if (
        currentStroke &&
        currentStroke !== 'none' &&
        (currentStroke === '#000000' ||
          currentStroke === '#000' ||
          currentStroke === 'black' ||
          currentStroke === 'rgb(0, 0, 0)')
      ) {
        element.setAttribute('stroke', rgbaColor);
      }

      // 子要素の処理
      Array.from(element.children).forEach(processElement);
    };

    // 色の適用を実行
    processElement(contentClone);

    // パターンコンテンツの生成
    let patternContent = '';
    Array.from(contentClone.children).forEach((child) => {
      patternContent += new XMLSerializer().serializeToString(child);
    });

    // 元のSVG要素から必要な属性を継承しつつsymbolとして出力
    const attributes = Array.from(svgElement.attributes)
      .filter((attr) => !['id', 'viewBox'].includes(attr.name))
      .map((attr) => `${attr.name}="${attr.value}"`)
      .join(' ');

    // デバッグ用のログ出力
    console.log(`Creating pattern for layer ${id} with color ${color}`);
    console.log('Pattern content preview:', patternContent.substring(0, 100));

    return `<symbol id="${id}" viewBox="${viewBox}" ${attributes}>${patternContent}</symbol>`;
  } catch (error) {
    console.error('Error creating pattern definition:', error);
    return '';
  }
}

// SVG用にパターン要素を生成
function generatePatternElementsForSvg(
  layer: Layer,
  width: number,
  height: number
): PatternElement[] {
  return generatePatternElements(layer, width, height);
}

async function generateBackgroundImageForSvg(
  backgroundImage: string | null,
  width: number,
  height: number
): Promise<string> {
  // 背景画像がない場合は空文字列を返す
  if (!backgroundImage) {
    return '';
  }

  try {
    // 画像をBase64形式に変換
    const base64Background = await getBase64Image(backgroundImage);

    // SVG image要素を生成
    return `
  <image
    x="0"
    y="0"
    width="${width}"
    height="${height}"
    preserveAspectRatio="xMidYMid meet"
    xlink:href="${base64Background}"
  />`;
  } catch (error) {
    // エラーが発生した場合はログを出力して空文字列を返す
    console.error('Failed to process background image:', error);
    return '';
  }
}

function generateSvgDefs(layers: Layer[]): string {
  let defsContent = '\n  <defs>';

  // クリップパス定義の生成
  const layersWithClipMask = layers.filter((layer) => layer.clipMask);
  layersWithClipMask.forEach((layer) => {
    if (layer.clipMask) {
      defsContent += `
    <clipPath id="clip-${layer.id}">
      <rect
        x="${layer.clipMask.x}"
        y="${layer.clipMask.y}"
        width="${layer.clipMask.width}"
        height="${layer.clipMask.height}"
      />
    </clipPath>`;
    }
  });

  //　パターン定義の生成
  const validLayers = layers.filter((layer) => layer.svgPattern);
  validLayers.forEach((layer) => {
    if (layer.svgPattern) {
      const defId = `pattern-${layer.id}-${layer.parameters.color.replace(
        '#',
        ''
      )}`;
      const patternDef = createPatternDef(
        layer.svgPattern,
        layer.parameters.color,
        defId
      );
      if (patternDef) {
        defsContent += '\n    ' + patternDef;
      }
    }
  });

  defsContent += '\n  </defs>';
  return defsContent;
}

export async function generateSVG(
  layers: Layer[],
  width: number,
  height: number,
  backgroundImage: string | null
): Promise<string> {
  // SVGドキュメントのヘッダー
  let svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">
<svg xmlns="http://www.w3.org/2000/svg" 
     xmlns:xlink="http://www.w3.org/1999/xlink"
     width="${width}" 
     height="${height}" 
     viewBox="0 0 ${width} ${height}">`;

  // 背景画像の処理
  const backgroundImageSvg = await generateBackgroundImageForSvg(
    backgroundImage,
    width,
    height
  );
  svgContent += backgroundImageSvg;

  // パターン定義セクション
  const svgDefs = generateSvgDefs(layers);
  svgContent += svgDefs;

  // レイヤーごとのパターン描画
  const validLayers = layers.filter((layer) => layer.svgPattern);
  validLayers.forEach((layer) => {
    if (!layer.svgPattern || !layer.visible) return null;

    const defId = `pattern-${layer.id}-${layer.parameters.color.replace(
      '#',
      ''
    )}`;

    // パターン要素の生成
    const patterns = generatePatternElementsForSvg(layer, width, height);

    // レイヤーの回転
    const angleView = layer.parameters.angleView;

    // クリッピングパスの適用
    const clipPathAttr = layer.clipMask
      ? ` clip-path="url(#clip-${layer.id})"`
      : '';

    // 中心点の計算
    const centerX = width / 2;
    const centerY = height / 2;

    svgContent += `
  <g${clipPathAttr}>
    <g transform="translate(${centerX},${centerY}) rotate(${angleView}) translate(${-centerX},${-centerY})">`;

    // 各パターンの描画
    patterns.forEach(({ transform, size }) => {
      svgContent += `
      <use xlink:href="#${defId}"
           width="${size}"
           height="${size}"
           transform="${transform}" />`;
    });

    svgContent += '\n    </g>';
    svgContent += '\n  </g>';
  });

  svgContent += '\n</svg>';
  return svgContent;
}

// SVGファイルのダウンロード関数
export function downloadSVG(svgContent: string, layers: Layer[]) {
  const fileName = generateFileName(layers);
  const blob = new Blob([svgContent], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
